import React, { useEffect, useState } from 'react';
import { MapPin, Menu as MenuIcon, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '@/components/public/Logo';
import { DEFAULT_SITE_CONTENT, mergeContent } from '@/constants/defaultSiteContent';

const isRouteTarget = (t) => typeof t === 'string' && t.startsWith('/');

const Navbar = ({ brand, content }) => {
  const c = mergeContent(DEFAULT_SITE_CONTENT.navbar, content || {});
  const menu = (c.menu && c.menu.length) ? c.menu : DEFAULT_SITE_CONTENT.navbar.menu;
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(menu[0]?.target || 'home');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      const y = window.scrollY + 120;
      let cur = menu[0]?.target || 'home';
      for (const l of menu) {
        if (isRouteTarget(l.target)) continue; // skip route links from scroll-spy
        const el = document.getElementById(l.target);
        if (el && el.offsetTop <= y) cur = l.target;
      }
      setActive(cur);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [menu]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header data-testid="public-navbar" className={`fixed top-0 inset-x-0 z-40 transition-colors ${scrolled || open ? 'bg-[#F5F0E6]/95 backdrop-blur border-b border-black/5 shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-[1360px] mx-auto flex items-center justify-between px-4 sm:px-6 py-3 sm:py-5 lg:py-6 gap-3">
        <a href="#home" aria-label={c.brand} className="shrink-0 min-w-0" onClick={close}><Logo brand={brand} showWordmark tagline /></a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {menu.map((l) => {
            const inner = (
              <>
                {l.label}
                {active === l.target && !isRouteTarget(l.target) && <span className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-[#0F2E23]" />}
              </>
            );
            const cls = `relative text-[11px] tracking-[0.22em] font-semibold transition-colors ${active === l.target ? 'text-[#0F2E23]' : 'text-[#141414]/70 hover:text-[#0F2E23]'}`;
            return isRouteTarget(l.target)
              ? <Link key={l.target} to={l.target} className={cls}>{inner}</Link>
              : <a key={l.target} href={`#${l.target}`} className={cls}>{inner}</a>;
          })}
        </nav>

        {/* Location pill (desktop only) */}
        <a href="#contact" className="hidden lg:inline-flex items-center gap-2 bg-[#0F2E23] text-[#F5F0E6] pl-3 pr-4 py-2.5 rounded-md shadow-sm hover:bg-[#153F30] transition-colors shrink-0">
          <MapPin className="w-4 h-4 text-[#C89B3C]" />
          <span className="leading-tight text-[11px] tracking-[0.18em] font-semibold">
            {c.location_pill_line1}<br/><span className="opacity-80 text-[10px]">{c.location_pill_line2}</span>
          </span>
        </a>

        {/* Mobile hamburger */}
        <button
          data-testid="navbar-mobile-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex w-11 h-11 items-center justify-center rounded-xl border border-[#0F2E23]/15 bg-[#F5F0E6] text-[#0F2E23] shadow-sm active:scale-[0.97] transition"
        >
          {open ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu sheet */}
      <div
        className={`lg:hidden fixed inset-x-0 top-[68px] bottom-0 z-30 transition-all duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-[#0F2E23]/40 backdrop-blur-sm" onClick={close} />
        <div
          className={`absolute inset-x-0 top-0 bg-[#F5F0E6] shadow-2xl border-b border-black/5 transition-transform duration-300 ${open ? 'translate-y-0' : '-translate-y-4'}`}
        >
          <nav data-testid="navbar-mobile-menu" className="max-w-[1360px] mx-auto px-4 py-4 flex flex-col">
            {menu.map((l) => {
              const cls = `flex items-center justify-between py-3.5 px-2 border-b border-black/5 text-[13px] tracking-[0.22em] font-semibold transition-colors ${active === l.target ? 'text-[#0F2E23]' : 'text-[#141414]/80'}`;
              const inner = (<>
                <span>{l.label}</span>
                {active === l.target && !isRouteTarget(l.target) && <span className="w-1.5 h-1.5 rounded-full bg-[#0F2E23]" />}
              </>);
              return isRouteTarget(l.target)
                ? <Link key={l.target} to={l.target} onClick={close} data-testid={`navbar-mobile-link-${l.target.replace(/\//g,'-')}`} className={cls}>{inner}</Link>
                : <a key={l.target} href={`#${l.target}`} onClick={close} data-testid={`navbar-mobile-link-${l.target}`} className={cls}>{inner}</a>;
            })}
            <a
              href="#contact"
              onClick={close}
              className="mt-4 inline-flex items-center gap-2 bg-[#0F2E23] text-[#F5F0E6] px-4 py-3 rounded-md text-[11px] tracking-[0.22em] font-semibold shadow-sm hover:bg-[#153F30] transition-colors self-start"
            >
              <MapPin className="w-4 h-4 text-[#C89B3C]" />
              <span className="leading-tight text-left">
                {c.location_pill_line1}<br/><span className="opacity-80 text-[10px]">{c.location_pill_line2}</span>
              </span>
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
